import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { ValidationError } from 'utils';
import fetchData from 'utils/fetchData';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';
import { useTranslation } from 'react-i18next';
import { session } from 'session';

type Props = {
  children: ReactNode;
};

type AuthContextType = {
  login: (email: string, password: string) => Promise<void>;
  crossLoginAuth: (token: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const useLegacyLoginAuth = (): AuthContextType => useContext(AuthContext);

export function LEGACY_LOGIN_AuthProvider({ children }: Props): JSX.Element {
  const { t } = useTranslation();
  const { showError } = useError();
  const { error } = useSmartlook();

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const encodedCredentials = window.btoa(`${email}:${password}`);
        const url = import.meta.env['VITE_LEGACY_LOGIN_API'];
        const res = await fetch(`${url}/token/basic`, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${encodedCredentials}`,
          },
        });

        if (res.status === 401) {
          throw new ValidationError([
            {
              message: 'Invalid credentials',
              messageTemplate: 'invalid_credentials',
              property: 'password',
            },
          ]);
        }

        if (!res.ok) {
          throw new Error(res.status.toString());
        }

        const data = await res.json();

        session.signInLegacy({ username: data.username, token: data.token });
      } catch (e) {
        error('[AuthProvider][login]', e);
        if (e instanceof ValidationError) {
          showError({
            message: t('error:unauthorized'),
            type: 'notification',
          });
          throw e;
        }
      }
    },
    [error, showError],
  );

  const crossLoginAuth = useCallback(
    async (oneTimeToken: string) => {
      try {
        const response = await fetchData('/token/one-time', {
          method: 'post',
          body: JSON.stringify({
            oneTimeToken,
          }),
        });

        if (!response) {
          throw new Error('No response received from server');
        }

        const { username, token } = response;

        if (!username || !token) {
          throw new Error('Invalid response: missing username or token');
        }

        session.signInLegacy({ username, token });
      } catch (e) {
        error('[AuthProvider][crossLoginAuth]', e);
        throw e;
      }
    },
    [error],
  );

  const profileContextValue = useMemo(
    () => ({
      login,
      crossLoginAuth,
    }),
    [login, crossLoginAuth],
  );

  return (
    <AuthContext.Provider value={profileContextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default useLegacyLoginAuth;
